import type { ReactNode } from 'react';
import { Box } from '@mui/material';

import { Header } from './Header';
import { Footer } from './Footer';
import { ConsentBanner } from '../consent/ConsentBanner';
import { ConsentManagedSync } from '../consent/ConsentManagedSync';
import { GoogleAnalytics } from '../analytics/GoogleAnalytics';

interface LayoutProps {
  children: ReactNode;
}

/**
 * Global page chrome: skip link, header, main, footer.
 *
 * - The skip link targets `#main-content` so keyboard users can bypass
 *   the header (WCAG 2.4.1).
 * - `<main>` carries `tabIndex={-1}` so the skip link can move focus to
 *   it without making it part of the regular tab order.
 * - Layout is a flex column so the footer can stick to the bottom on
 *   short pages without absolute positioning.
 * - `<ConsentManagedSync />` renders nothing — it reconciles the local
 *   consent store with the server-stored record (§10.7) when the user
 *   is signed in.
 * - `<ConsentBanner />` sits at the bottom of the document and is
 *   unmounted entirely once the user has expressed a consent choice,
 *   so it doesn't introduce layout drift on subsequent renders.
 */
export function Layout({ children }: LayoutProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <ConsentManagedSync />
      {/* Loads GTM only after analytics consent is granted (§10.7, rule 1). */}
      <GoogleAnalytics />
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <Header />
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{ flex: 1, outline: 'none', px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}
      >
        {children}
      </Box>
      <Footer />
      <ConsentBanner />
    </Box>
  );
}

export default Layout;
